/* =====================================================================
   Charm Optical — Global Search Overlay
   Vanilla, self-contained. No jQuery/library dependency.
   Predictive Search (primary) + lazy fuzzy fallback (typo tolerant).
   All product-derived text/attrs are escaped before hitting the DOM.
   ===================================================================== */
(function () {
  'use strict';

  var root = document.getElementById('csSearch');
  if (!root) return;

  var input   = document.getElementById('csSearchInput');
  var listEl  = document.getElementById('csSearchResults');
  var footEl  = document.getElementById('csSearchFoot');
  var statusEl = document.getElementById('csSearchStatus');
  var echoEl  = root.querySelector('[data-cs-echo]');
  var panel   = root.querySelector('.cs-search__panel');

  var DEBOUNCE   = 170;
  var LIMIT      = 8;
  /* Prebuilt compact index (assets/search-index.json, ~85 KB gz, every active
     product) replaces the old /products.json crawl (~35 MB, capped at 3,000 of
     3,329 products, never ready for the first keystroke). Built by
     scripts/build-search-index.mjs, refreshed nightly; the asset URL carries a
     content hash, so a rebuild invalidates the localStorage copy by itself.
     Matching rules live in search-core.js (loaded first; testable in Node). */
  var Core       = window.CharmSearchCore || null;
  var INDEX_URL  = root.getAttribute('data-cs-index') || '';
  var INDEX_KEY  = 'cs_prod_index_v2';
  var INDEX_WAIT = 1500;         // ms a query waits for the index before answering without it
  var IMG_BASE   = 'https://cdn.shopify.com/s/files/1/0552/6759/0341/';

  var state = {
    open: false,
    q: '',
    activeIndex: -1,   // -1 = input, 0..n-1 = rows, rows.length = see-all
    rows: [],          // rendered anchor elements (results only)
    controller: null,  // in-flight predictive AbortController
    reqId: 0,
    index: null,       // prepared index entries once loaded
    indexPromise: null,
    seeAllQ: '',       // model codes ("RB2140 OR VE4515") for the See-all link when the raw query would find nothing
    lastFocus: null
  };
  var priceCache = {}; // handle → live price string (index prices can be a day old)

  /* ------------------------------------------------------------------ */
  /* helpers                                                            */
  /* ------------------------------------------------------------------ */
  function escHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  // for use inside a double-quoted attribute
  function escAttr(s) { return escHtml(s); }
  // only allow same-origin relative product/collection paths to be used as href/src
  function safeUrl(u) {
    u = String(u == null ? '' : u);
    if (/^\/[^\/]/.test(u) || u.indexOf('/products/') === 0 || u.indexOf('/collections/') === 0) return u;
    if (/^https:\/\/(cdn\.shopify\.com|charmoptical\.ca)\//.test(u)) return u;
    // same-origin absolute paths only — reject protocol-relative "//host" which
    // browsers resolve cross-origin (the earlier /^\/[^\/]/ test already admits
    // legit "/x" paths, so this just guards the leading-double-slash case).
    return (u.charAt(0) === '/' && u.charAt(1) !== '/') ? u : '';
  }
  // Ray-Ban Meta / Oakley Meta smart glasses have bespoke landing pages
  // (/pages/rayban-meta, /pages/oakley-meta) — the default /products/ template
  // is NOT the customized experience. Route Meta results to their custom page.
  // Ray-Ban Meta product handle → the page's ?model= key, so a result opens on that frame
  var RB_META_MODEL = {
    'ray-ban-meta-wayfarer-1': 'wayfarer-50',
    'ray-ban-meta-headliner': 'headliner',
    'ray-ban-meta-headliner-asian-fit': 'headliner-af',
    'ray-ban-meta-skyler': 'skyler',
    'ray-ban-meta-blayzer-optics': 'blayzer',
    'ray-ban-meta-scriber-optics': 'scriber',
    'ray-ban-meta-wayfarer-gen-3': 'g3-wayfarer',
    'ray-ban-meta-wayfarer-optics-gen-3': 'g3-wayfarer-optics',
    'ray-ban-meta-zena-gen-3': 'g3-zena',
    'ray-ban-meta-zena-optics-gen-3': 'g3-zena-optics',
    'ray-ban-meta-aviator-gen-3': 'g3-aviator',
    'ray-ban-meta-aviator-optics-gen-3': 'g3-aviator-optics',
    'ray-ban-meta-audio-clubmaster': 'audio-clubmaster',
    'ray-ban-meta-audio-clubmaster-optics': 'audio-clubmaster-optics',
    'ray-ban-meta-audio-burbank': 'audio-burbank',
    'ray-ban-meta-audio-burbank-optics': 'audio-burbank-optics'
  };
  function landingFor(p) {
    var h = String((p && (p.url || p.handle)) || '').toLowerCase();
    var t = String((p && (p.title || p.vendor)) || '').toLowerCase();
    // whole word only — "metal" frames (RB3025 Aviator Large Metal, Oakley Metalink…) are not Meta
    var isMeta = /(^|[^a-z])meta([^a-z]|$)/;
    if (!isMeta.test(h) && !isMeta.test(t)) return '';
    if (h.indexOf('oakley') !== -1 || t.indexOf('oakley') !== -1) return '/pages/oakley-meta';
    if (h.indexOf('ray') !== -1 || t.indexOf('ray') !== -1) {
      var hm = h.match(/products\/([^\/?#]+)/);
      var handle = hm ? hm[1] : h;
      var key = Object.prototype.hasOwnProperty.call(RB_META_MODEL, handle) ? RB_META_MODEL[handle] : '';
      return '/pages/rayban-meta' + (key ? '?model=' + key : '');
    }
    return '';
  }
  function sizedImg(u) {
    u = safeUrl(u);
    if (!u) return '';
    if (u.indexOf('cdn.shopify.com') === -1) return u;
    // inject width param for a lean thumbnail
    return u + (u.indexOf('?') === -1 ? '?' : '&') + 'width=140';
  }

  function normalize(s) {
    var str = String(s == null ? '' : s).toLowerCase();
    if (str.normalize) str = str.normalize('NFD').replace(/[̀-ͯ]/g, '');
    return str;
  }
  function norm(s) {
    return normalize(s).replace(/[^a-z0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim();
  }
  // Length-preserving lowercase + diacritic strip. Unlike norm(), it does NOT
  // collapse punctuation or whitespace, so every character index maps 1:1 back
  // onto the original string — required for slicing the raw title in highlight().
  function normAligned(s) {
    var str = String(s == null ? '' : s).toLowerCase();
    if (str.normalize) str = str.normalize('NFD').replace(/[̀-ͯ]/g, '');
    return str;
  }

  // brand / product aliases → canonical search term
  var ALIASES = [
    [/\b(ray\s?ban|rayban|ray\s?bans)\b/g, 'ray-ban'],
    [/\b(prada\s?sport|linea\s?rossa)\b/g, 'prada linea rossa'],
    [/\b(d\s?&\s?g|dg|dolce|dolce\s?gabbana|dolce\s?and\s?gabbana)\b/g, 'dolce gabbana'],
    [/\b(oakly|oakely|oakleys)\b/g, 'oakley'],
    [/\b(acuve|accuvue|acuvu|acuvues)\b/g, 'acuvue'],
    [/\b(biofinety|biofinit|biofinitys)\b/g, 'biofinity'],
    [/\b(gucci\s?glasses|guccy|guci)\b/g, 'gucci'],
    [/\b(contacts?|contact\s?lens(es)?|lenses)\b/g, 'contact lenses'],
    [/\b(bluelight|blue\s?cut|bluecut|blue\s?light)\b/g, 'blue light'],
    [/\b(sunglass(es)?|shades)\b/g, 'sunglasses'],
    [/\b(eyeglass(es)?|glasses|specs|frames?)\b/g, 'glasses']
  ];
  function applyAliases(q) {
    var s = ' ' + norm(q) + ' ';
    for (var i = 0; i < ALIASES.length; i++) s = s.replace(ALIASES[i][0], ALIASES[i][1]);
    return s.replace(/\s+/g, ' ').trim();
  }

  // bounded Levenshtein (early-exit at maxD)
  // (editDistance / fuzzy scoring moved to search-core.js — CharmSearchCore)

  function catLabel(type, title) {
    var t = norm(type);
    if (t.indexOf('sun') !== -1) return 'Sunglasses';
    if (t.indexOf('contact') !== -1 || t.indexOf('lens') !== -1) return 'Contact Lenses';
    if (t.indexOf('eyeglass') !== -1 || t.indexOf('glass') !== -1 || t.indexOf('optical') !== -1) return 'Glasses';
    if (type) return type;
    return 'Product';
  }
  function subLabel(vendor, type, title) {
    // Show the brand as a subtitle ONLY when it adds info the title doesn't
    // already carry. Otherwise return '' — never repeat the category, which is
    // already shown as the right-side tag (that was the duplicated "Glasses").
    var v = String(vendor || '').trim();
    if (v && norm(title).indexOf(norm(v)) === -1) return v;
    return '';
  }
  function money(p) {
    if (p == null || p === '') return '';
    var s = String(p);
    if (s.charAt(0) === '$') return s;
    var n = parseFloat(s);
    if (isNaN(n)) return '';
    // suggest.json / products.json give major units already ("206.00")
    return '$' + n.toFixed(2).replace(/\.00$/, '');
  }

  /* ------------------------------------------------------------------ */
  /* open / close + a11y                                                */
  /* ------------------------------------------------------------------ */
  var scrollY = 0;
  function open() {
    if (state.open) return;
    state.open = true;
    state.lastFocus = document.activeElement;
    root.hidden = false;
    // lock body scroll without layout jump
    scrollY = window.scrollY || window.pageYOffset || 0;
    document.body.style.top = '-' + scrollY + 'px';
    document.body.classList.add('cs-search-lock');
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    // next frame → transition in
    requestAnimationFrame(function () { root.classList.add('is-open'); });
    setView('empty');
    footEl && footEl.setAttribute('href', '/search');
    // focus input (delay so iOS keyboard behaves)
    setTimeout(function () { input.focus(); input.select && input.select(); }, 60);
    // kick off the fuzzy index lazily (non-blocking)
    ensureIndex();
    document.addEventListener('keydown', onKeydown, true);
  }

  function close() {
    if (!state.open) return;
    state.open = false;
    root.classList.remove('is-open');
    if (state.controller) { state.controller.abort(); state.controller = null; }
    document.removeEventListener('keydown', onKeydown, true);
    // wait for fade-out
    setTimeout(function () {
      if (state.open) return;
      root.hidden = true;
      // reset
      input.value = '';
      state.q = '';
      root.classList.remove('has-query', 'is-loading');
      listEl.innerHTML = '';
      setActive(-1);
      setView('empty');
    }, 300);
    // unlock scroll
    document.body.classList.remove('cs-search-lock');
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, scrollY);
    // restore focus
    if (state.lastFocus && state.lastFocus.focus) {
      try { state.lastFocus.focus({ preventScroll: true }); } catch (e) { state.lastFocus.focus(); }
    }
  }

  function setView(v) {
    root.setAttribute('data-view', v);
    // The combobox only "owns" a visible listbox in the results view; reflect
    // that so AT users are notified when the popup opens and closes.
    if (input) input.setAttribute('aria-expanded', v === 'results' ? 'true' : 'false');
  }

  // Visually-hidden live region — announces result counts / no-match state
  // to screen readers (WCAG 2.2 SC 4.1.3 Status Messages).
  function announce(msg) { if (statusEl) statusEl.textContent = msg || ''; }

  /* ------------------------------------------------------------------ */
  /* keyboard                                                           */
  /* ------------------------------------------------------------------ */
  function focusables() {
    return Array.prototype.slice.call(
      root.querySelectorAll('a[href], button, input, [tabindex]:not([tabindex="-1"])')
    ).filter(function (el) { return el.offsetParent !== null && !el.disabled; });
  }

  function onKeydown(e) {
    if (!state.open) return;
    var k = e.key;

    if (k === 'Escape') { e.preventDefault(); close(); return; }

    // focus trap on Tab
    if (k === 'Tab') {
      var f = focusables();
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      return;
    }

    var nav = navTargets();
    if (k === 'ArrowDown') {
      e.preventDefault();
      if (!nav.length) return;
      state.activeIndex = state.activeIndex + 1 >= nav.length ? 0 : state.activeIndex + 1;
      setActive(state.activeIndex, nav);
    } else if (k === 'ArrowUp') {
      e.preventDefault();
      if (!nav.length) return;
      state.activeIndex = state.activeIndex - 1 < 0 ? nav.length - 1 : state.activeIndex - 1;
      setActive(state.activeIndex, nav);
    } else if (k === 'Enter') {
      var nav2 = navTargets();
      if (state.activeIndex >= 0 && nav2[state.activeIndex]) {
        e.preventDefault();
        var href = nav2[state.activeIndex].getAttribute('href');
        if (href) window.location.href = href;
      } else if (state.q.trim()) {
        e.preventDefault();
        goSeeAll();
      }
    } else if (k === 'Home' && document.activeElement === input) {
      // let native caret behaviour work
    }
  }

  // navigable targets = result rows + (see-all if query)
  function navTargets() {
    var t = state.rows.slice();
    if (root.classList.contains('has-query') && footEl && footEl.offsetParent !== null) t.push(footEl);
    return t;
  }

  function setActive(idx, nav) {
    nav = nav || navTargets();
    state.activeIndex = idx;
    nav.forEach(function (el, i) {
      var on = i === idx;
      var isOption = el.getAttribute('role') === 'option';
      el.classList.toggle('is-active', on);
      // aria-selected is only valid on role="option" elements. Both the result
      // rows and the see-all footer are exposed to the listbox as options
      // (footer via aria-owns on #csSearchResults), so activedescendant always
      // resolves to a real member of the controlled listbox.
      if (isOption) el.setAttribute('aria-selected', on ? 'true' : 'false');
      if (on) {
        if (isOption && el.id) input.setAttribute('aria-activedescendant', el.id);
        else input.removeAttribute('aria-activedescendant');
        if (el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
      }
    });
    if (idx < 0) input.removeAttribute('aria-activedescendant');
  }

  // Shopify's /search page can't find "4515" either — when the index matched a
  // model number that Shopify's own search would miss, send the lettered codes
  // instead ("VE4515", "RB2140 OR RB2140F"); it understands OR.
  function seeAllHref(q) {
    q = (q || '').trim();
    if (!q) return '/search';
    if (state.seeAllQ) return '/search?q=' + encodeURIComponent(state.seeAllQ) + '&type=product';
    return '/search?q=' + encodeURIComponent(q) + '&type=product&options[prefix]=last';
  }
  function goSeeAll() {
    var q = state.q.trim();
    if (!q) return;
    window.location.href = seeAllHref(q);
  }

  /* ------------------------------------------------------------------ */
  /* rendering                                                          */
  /* ------------------------------------------------------------------ */
  function highlight(title, q) {
    // Search a LENGTH-PRESERVING copy of the title so match offsets line up
    // with the raw string we slice. (norm() collapses punctuation/whitespace,
    // which shifts indices and drops <mark> on the wrong characters.)
    var hs = normAligned(title), nq = norm(q);
    if (!nq) return escHtml(title);
    var idx = hs.indexOf(nq), len = nq.length;
    if (idx === -1) {
      // fall back to the first query token
      var tok = nq.split(' ')[0];
      idx = tok ? hs.indexOf(tok) : -1;
      len = tok.length;
      if (idx === -1) return escHtml(title);
    }
    return escHtml(title.slice(0, idx)) + '<mark>' + escHtml(title.slice(idx, idx + len)) + '</mark>' + escHtml(title.slice(idx + len));
  }

  function render(items, q) {
    // If a result row currently holds DOM focus, replacing innerHTML would drop
    // focus to <body> and break the focus trap — pull focus back to the input.
    var refocus = listEl.contains(document.activeElement);
    if (!items.length) {
      listEl.innerHTML = '';
      state.rows = [];
      setView('none');
      setActive(-1);
      if (refocus) input.focus();
      announce('No exact match for “' + (q || '').trim() + '” — showing close picks.');
      return;
    }
    var html = '';
    for (var i = 0; i < items.length; i++) {
      var p = items[i];
      var url = safeUrl(landingFor(p) || p.url);
      if (!url) continue;
      var img = sizedImg(p.image);
      var sub = subLabel(p.vendor, p.type, p.title);
      var cat = catLabel(p.type, p.title);
      var price = money(p.price);
      html +=
        '<a class="cs-search__row" id="csRow' + i + '" role="option" aria-selected="false" href="' + escAttr(url) + '"' +
          (p.fromIndex && p.handle ? ' data-cs-handle="' + escAttr(p.handle) + '"' : '') + '>' +
          (img
            ? '<img class="cs-search__thumb" src="' + escAttr(img) + '" alt="" loading="lazy" width="52" height="52">'
            : '<span class="cs-search__thumb" aria-hidden="true"></span>') +
          '<span class="cs-search__meta">' +
            '<span class="cs-search__title">' + highlight(p.title, q) + '</span>' +
            (sub ? '<span class="cs-search__sub">' + escHtml(sub) + '</span>' : '') +
          '</span>' +
          '<span class="cs-search__tail">' +
            (price ? '<span class="cs-search__price">' + escHtml(price) + '</span>' : '') +
            '<span class="cs-search__cat">' + escHtml(cat) + '</span>' +
            '<i class="ri-arrow-right-line cs-search__arrow" aria-hidden="true"></i>' +
          '</span>' +
        '</a>';
    }
    listEl.innerHTML = html;
    state.rows = Array.prototype.slice.call(listEl.querySelectorAll('.cs-search__row'));
    // hover syncs the active index for a coherent keyboard/mouse model
    state.rows.forEach(function (row, i) {
      row.addEventListener('mousemove', function () {
        if (state.activeIndex !== i) setActive(i);
      });
    });
    setView('results');
    setActive(-1);
    if (refocus) input.focus();
    var n = state.rows.length;
    announce(n + (n === 1 ? ' result' : ' results') + ' for “' + (q || '').trim() + '”.');
    refreshPrices();
  }

  // Index rows carry the price as of the last nightly build. Swap in the live
  // price for the handful of rows on screen (one tiny /products/<handle>.js each,
  // cached per session) so a same-day reprice never shows stale on the overlay.
  function refreshPrices() {
    var rows = listEl.querySelectorAll('[data-cs-handle]');
    Array.prototype.forEach.call(rows, function (row) {
      var h = row.getAttribute('data-cs-handle');
      var el = row.querySelector('.cs-search__price');
      if (!h || !el) return;
      var apply = function (p) { var m = money(p); if (m && el.isConnected) el.textContent = m; };
      if (priceCache[h] != null) { apply(priceCache[h]); return; }
      fetch('/products/' + encodeURIComponent(h) + '.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (j) {
          if (!j || typeof j.price !== 'number') return;
          priceCache[h] = (j.price / 100).toFixed(2);
          apply(priceCache[h]);
        })
        .catch(function () {});
    });
  }

  /* ------------------------------------------------------------------ */
  /* predictive search (primary)                                        */
  /* ------------------------------------------------------------------ */
  function predictive(q) {
    if (state.controller) state.controller.abort();
    var controller = ('AbortController' in window) ? new AbortController() : null;
    state.controller = controller;
    /* Shopify's predictive search defaults to fields = title, product_type,
       variants.title, vendor — which means a SKU or a scanned barcode returned NOTHING.
       Verified live 2026-07-27 against Prada PR D07S:
         "PR D07S"          -> 1 hit          (matched the title)
         "0PR D07S_16K08Z"  -> NO RESULTS     (its own SKU)
         "8056262866955"    -> NO RESULTS     (its own GTIN)
       Adding the fields param below turns both into a hit. Naming ANY field replaces the
       whole default set, so the four defaults are repeated here — dropping one silently
       breaks ordinary title search.
       Also verified: matching is case-insensitive and works on a partial SKU, so
       "d07s_16k08z" finds it too. A squashed code ("PRD07S") still will not match —
       Shopify tokenises on the separators, so there is no such token to match. */
    var url = '/search/suggest.json?q=' + encodeURIComponent(q) +
              '&resources[type]=product&resources[limit]=' + LIMIT +
              '&resources[options][unavailable_products]=last' +
              '&resources[options][fields]=' + encodeURIComponent(
                'title,product_type,variants.title,vendor,variants.sku,variants.barcode,tag');
    return fetch(url, { signal: controller ? controller.signal : undefined, headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) {
        state.controller = null;
        if (!j || !j.resources || !j.resources.results) return [];
        var prods = j.resources.results.products || [];
        return prods.map(function (p) {
          return {
            title: p.title,
            vendor: p.vendor,
            type: p.type,
            url: p.url,
            handle: p.handle,
            price: p.price,
            image: (p.featured_image && p.featured_image.url) || p.image || ''
          };
        });
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') throw err;
        return [];
      });
  }

  /* ------------------------------------------------------------------ */
  /* fuzzy fallback index (lazy, session-cached)                        */
  /* ------------------------------------------------------------------ */
  // Resolves to the prepared index (or null if unavailable). Safe to call often.
  function ensureIndex() {
    if (state.indexPromise) return state.indexPromise;
    if (!Core || !INDEX_URL || typeof fetch !== 'function') {
      state.indexPromise = Promise.resolve(null);
      return state.indexPromise;
    }
    state.indexPromise = new Promise(function (resolve) {
      try {
        var cached = localStorage.getItem(INDEX_KEY);
        if (cached) {
          var c = JSON.parse(cached);
          if (c && c.url === INDEX_URL && c.rows && c.rows.length) {
            state.index = Core.prepare(c.rows);
            resolve(state.index);
            return;
          }
        }
      } catch (e) {}
      fetch(INDEX_URL, { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (j) {
          var rows = (j && j[1]) || null;
          if (!rows || !rows.length) { resolve(null); return; }
          state.index = Core.prepare(rows);
          try { localStorage.setItem(INDEX_KEY, JSON.stringify({ url: INDEX_URL, rows: rows })); } catch (e) {}
          resolve(state.index);
        })
        .catch(function () { resolve(null); });
    });
    return state.indexPromise;
  }
  function withTimeout(p, ms) {
    return Promise.race([p, new Promise(function (r) { setTimeout(function () { r(null); }, ms); })]);
  }

  // index entry → result row (same shape predictive() returns)
  function toResult(e) {
    return {
      title: e.t, vendor: e.v, type: e.y,
      url: '/products/' + e.h, handle: e.h,
      price: priceCache[e.h] != null ? priceCache[e.h] : e.p,
      image: e.i ? (/^https?:\/\//.test(e.i) ? e.i : IMG_BASE + e.i) : '',
      fromIndex: true
    };
  }

  // Model-number lookup: "4515" → Versace VE4515, "2140" → every RB2140,
  // "ve 4515" / "0VE4515" / "rb4461" all resolve. Returns {strong, weak}:
  // strong = exact/prefix code hits that must outrank Shopify's guesses.
  function codeResults(sq) {
    if (!state.index || !Core) return { strong: [], weak: [], hits: [] };
    var hits = Core.codeSearch(state.index, sq, LIMIT);
    var strong = [], weak = [];
    for (var i = 0; i < hits.length; i++) (hits[i].score <= 3 ? strong : weak).push(toResult(hits[i].entry));
    return { strong: strong, weak: weak, hits: hits };
  }

  // Free-text fallback (typo tolerant) over the whole index.
  function fuzzySearch(q) {
    if (!state.index || !Core) return [];
    // aliases may emit punctuation (e.g. "ray-ban"); re-normalize so tokens
    // line up with the hyphen-stripped haystack the core scores against.
    var nq = norm(applyAliases(q)) || norm(q);
    if (!nq) return [];
    return Core.textSearch(state.index, nq, LIMIT).map(function (h) { return toResult(h.entry); });
  }

  function mergeResults(primary, fuzzy) {
    var out = [], seen = {};
    function key(p) { return safeUrl(p.url).split('?')[0]; }
    for (var i = 0; i < primary.length; i++) {
      var k = key(primary[i]); if (!k || seen[k]) continue; seen[k] = 1; out.push(primary[i]);
    }
    for (var j = 0; j < fuzzy.length && out.length < LIMIT; j++) {
      var k2 = key(fuzzy[j]); if (!k2 || seen[k2]) continue; seen[k2] = 1; out.push(fuzzy[j]);
    }
    return out.slice(0, LIMIT);
  }

  /* ------------------------------------------------------------------ */
  /* query pipeline                                                     */
  /* ------------------------------------------------------------------ */
  var timer = null;
  function onInput() {
    var q = input.value;
    state.q = q;
    var has = q.trim().length > 0;
    root.classList.toggle('has-query', has);
    if (echoEl) echoEl.textContent = q.trim();
    if (footEl) footEl.setAttribute('href', has ? '/search?q=' + encodeURIComponent(q.trim()) + '&type=product&options[prefix]=last' : '/search');

    clearTimeout(timer);
    if (!has) {
      if (state.controller) { state.controller.abort(); state.controller = null; }
      root.classList.remove('is-loading');
      listEl.innerHTML = '';
      state.rows = [];
      setActive(-1);
      setView('empty');
      announce('');
      return;
    }
    root.classList.add('is-loading');
    timer = setTimeout(function () { runQuery(q); }, DEBOUNCE);
  }

  function runQuery(q) {
    var myReq = ++state.reqId;
    var sq = Core ? Core.codeQuery(q) : '';
    // Predictive and the index load run side by side; the index is normally
    // already in localStorage, and never holds a query longer than INDEX_WAIT.
    var primaryP = predictive(q).then(null, function (err) {
      if (err && err.name === 'AbortError') throw err;
      return [];                                           // network failed → index only
    });
    Promise.all([primaryP, withTimeout(ensureIndex(), INDEX_WAIT)])
      .then(function (arr) {
        if (myReq !== state.reqId) return;                 // stale
        var primary = arr[0] || [];
        var results, seeAllQ = '';
        if (sq) {
          // Model number: exact/prefix code hits first, then Shopify's hits,
          // then looser code hits. "4461d" no longer surfaces Diesel DL4021D
          // above the Ray-Ban RB4461D the customer is holding.
          var cr = codeResults(sq);
          results = mergeResults(cr.strong, mergeResults(primary, cr.weak));
          if (!primary.length && cr.strong.length) seeAllQ = Core.seeAllQuery(cr.hits);
        } else {
          results = primary;
          if (primary.length < LIMIT) results = mergeResults(primary, fuzzySearch(q));
        }
        state.seeAllQ = seeAllQ;
        if (footEl) footEl.setAttribute('href', seeAllHref(q));
        root.classList.remove('is-loading');
        render(results, q);
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;      // superseded
        if (myReq !== state.reqId) return;
        state.seeAllQ = '';
        root.classList.remove('is-loading');
        render(fuzzySearch(q), q);
      });
  }

  /* ------------------------------------------------------------------ */
  /* wiring                                                             */
  /* ------------------------------------------------------------------ */
  // any .search-trigger opens (four buttons across both headers)
  document.addEventListener('click', function (e) {
    var trig = e.target.closest && e.target.closest('.search-trigger');
    if (trig) { e.preventDefault(); open(); }
  });

  // keyboard activation of the trigger (it's an <a> without href) + "/" shortcut
  document.addEventListener('keydown', function (e) {
    if (state.open) return;
    var trig = e.target.closest && e.target.closest('.search-trigger');
    if (trig && (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar')) {
      e.preventDefault(); open(); return;
    }
    if (e.key === '/' && !/^(INPUT|TEXTAREA|SELECT)$/.test((e.target.tagName || '')) && !e.target.isContentEditable) {
      e.preventDefault(); open();
    }
  });

  // close on backdrop / stage-padding click (targets outside the inner column),
  // and on any explicit close control (Cancel button).
  var backdrop = root.querySelector('.cs-search__backdrop');
  var stage    = root.querySelector('.cs-search__stage');
  function outsideClose(e) { if (e.target === backdrop || e.target === stage) close(); }
  if (backdrop) backdrop.addEventListener('click', outsideClose);
  if (stage)    stage.addEventListener('click', outsideClose);
  root.querySelectorAll('.cs-search__cancel').forEach(function (btn) {
    btn.addEventListener('click', function (e) { e.preventDefault(); close(); });
  });

  input.addEventListener('input', onInput);

  // clear button
  var clearBtn = root.querySelector('[data-cs-clear]');
  if (clearBtn) clearBtn.addEventListener('click', function () {
    input.value = ''; onInput(); input.focus();
  });

  // popular-search chips (buttons carry data-cs-term; anchors just navigate)
  root.addEventListener('click', function (e) {
    var chip = e.target.closest('[data-cs-term]');
    if (chip) {
      input.value = chip.getAttribute('data-cs-term');
      onInput();
      input.focus();
    }
  });

  // see-all footer click
  if (footEl) footEl.addEventListener('click', function (e) {
    e.preventDefault(); goSeeAll();
  });

  // Warm the index once the page is idle (~85 KB gz, then localStorage), so the
  // very first keystroke — the one staff type with a customer waiting — answers
  // from the whole catalog instead of only Shopify's word-prefix search.
  function prefetchIndex() { ensureIndex(); }
  if ('requestIdleCallback' in window) requestIdleCallback(prefetchIndex, { timeout: 4000 });
  else setTimeout(prefetchIndex, 2500);

  // expose (harmless) for debugging / future triggers
  window.CharmSearch = { open: open, close: close, ensureIndex: ensureIndex };
})();
